/**
 * Receipt templates for the thermal printer (Iter 81).
 *
 * Each builder returns a { lines, cut, feedAfter } object consumed
 * by `thermalPrint()` in `lib/thermalPrinter.js`. Keeping the shape
 * pure/data-only means we can unit-test width wrapping without any
 * Bluetooth stack in scope.
 *
 * Note on ₹: ESC/POS default codepage 437 doesn't have the rupee
 * glyph, so we render "Rs." instead. Same rule as the existing
 * `InvoiceThermal.jsx` HTML fallback.
 */

const money = (v) => `Rs.${Number(v || 0).toFixed(2)}`;
const today = () => new Date().toLocaleString();

/** POS receipt printed on the staff phone after a walk-up sale. */
export function buildPosReceipt(result, ctx = {}) {
  const c = result.customer || {};
  const lines = [
    { text: "RESCIGO HEALTH", align: "center", size: 2, bold: true },
    { text: "Waste Management · POS", align: "center" },
    { divider: true },
    { row: ["Invoice", result.invoice_no || "-"] },
    { row: ["Date", today()] },
    { row: ["Status", (result.status || "issued").toUpperCase()] },
    { divider: true },
    { text: c.name || "Walk-up customer", bold: true },
    ...(c.phone ? [{ text: c.phone }] : []),
    ...(c.address ? [{ text: c.address }] : []),
    { divider: true },
    ...(result.bag_codes || []).map((code, i) => ({
      row: [`${i + 1}. ${code}`, ""],
    })),
    ...(result.bag_codes?.length ? [{ divider: true }] : []),
    { row: ["Bags", String((result.bag_codes || []).length)] },
    ...(result.total_kg ? [{ row: ["Weight", `${Number(result.total_kg).toFixed(2)} kg`] }] : []),
    ...(ctx.subtotal ? [{ row: ["Subtotal", money(ctx.subtotal)] }] : []),
    ...(ctx.gst ? [{ row: ["GST", money(ctx.gst)] }] : []),
    { divider: true },
    { row: ["TOTAL", money(result.total)], bold: true, size: "tall" },
    { blank: true },
    { text: (result.status === "draft"
        ? "Draft — office will verify & finalise"
        : "PAID · Thank you"), align: "center", bold: true },
    ...(ctx.staff_name ? [{ text: `Cashier: ${ctx.staff_name}`, align: "center" }] : []),
    { blank: true },
    { text: "www.rescigo.in", align: "center" },
  ];
  return { lines, cut: true, feedAfter: 4 };
}

/** Delivery slip printed when a bag order is handed over. */
export function buildDeliveryReceipt(chln) {
  const c = chln.customer || {};
  const items = chln.items || chln.bag_lines || [];
  const lines = [
    { text: "RESCIGO HEALTH", align: "center", size: 2, bold: true },
    { text: "Bag delivery slip", align: "center" },
    { divider: true },
    { row: ["Request", chln.request_no || chln.br_no || "-"] },
    { row: ["Challan", chln.challan_no || "-"] },
    { row: ["Date", today()] },
    { row: ["Status", (chln.status || "delivered").toUpperCase()] },
    { divider: true },
    { text: c.name || "Customer", bold: true },
    ...(c.phone ? [{ text: c.phone }] : []),
    ...(c.address ? [{ text: c.address }] : []),
    { divider: true },
    ...items.map((it) => ({
      row: [
        `${(it.category || it.name || "Bags").toString().slice(0, 18)}`,
        `${it.qty || it.quantity || 0} nos`,
      ],
    })),
    ...(items.length ? [{ divider: true }] : []),
    { row: ["Total bags", String((items || []).reduce((s, x) => s + Number(x.qty || x.quantity || 0), 0))] },
    ...(chln.delivery_pin ? [
      { blank: true },
      { text: `PIN VERIFIED: ${chln.delivery_pin}`, align: "center", bold: true },
    ] : []),
    ...(chln.signer_name ? [{ row: ["Received by", chln.signer_name] }] : []),
    { blank: true },
    { text: "Thank you", align: "center", bold: true },
    { text: "www.rescigo.in", align: "center" },
  ];
  return { lines, cut: true, feedAfter: 4 };
}

/** Quick print of a Service Request confirmation slip. */
export function buildSrReceipt(sr) {
  const c = sr.customer || {};
  const lines = [
    { text: "RESCIGO HEALTH", align: "center", size: 2, bold: true },
    { text: "Service Request", align: "center" },
    { divider: true },
    { row: ["SR No", sr.request_no || sr.id || "-"] },
    { row: ["Priority", (sr.priority || "NORMAL").toUpperCase()] },
    { row: ["Status", (sr.status || "").toUpperCase()] },
    { row: ["Date", sr.preferred_date || today().slice(0, 10)] },
    ...(sr.time_slot ? [{ row: ["Slot", sr.time_slot] }] : []),
    { divider: true },
    { text: c.name || sr.customer_name || "Customer", bold: true },
    ...(c.phone || sr.customer_phone ? [{ text: c.phone || sr.customer_phone }] : []),
    ...(c.address || sr.address ? [{ text: c.address || sr.address }] : []),
    { divider: true },
    ...(sr.package_name ? [{ row: ["Package", sr.package_name] }] : []),
    ...(sr.waste_category ? [{ row: ["Waste", sr.waste_category] }] : []),
    ...(sr.estimated_weight_kg ? [{ row: ["Est weight", `${sr.estimated_weight_kg} kg`] }] : []),
    ...(sr.description ? [{ blank: true }, { text: sr.description }] : []),
    { blank: true },
    { text: "Please keep this slip until pickup", align: "center" },
    { text: "www.rescigo.in", align: "center" },
  ];
  return { lines, cut: true, feedAfter: 4 };
}

/** Rescigo invoice — mirrors the HTML thermal view. */
export function buildInvoiceReceipt(inv) {
  const c = inv.customer || {};
  const items = inv.items || [];
  const lines = [
    { text: "RESCIGO HEALTH", align: "center", size: 2, bold: true },
    { text: "Tax Invoice", align: "center" },
    { divider: true },
    { row: ["Invoice", inv.invoice_no || "-"] },
    { row: ["Date", (inv.date || (inv.created_at || "").slice(0, 10) || today().slice(0, 10))] },
    ...(inv.due_date ? [{ row: ["Due", inv.due_date] }] : []),
    { divider: true },
    { text: c.name || "Customer", bold: true },
    ...(c.phone ? [{ text: c.phone }] : []),
    ...(c.address ? [{ text: c.address }] : []),
    ...(inv.service_request?.package_name
        ? [{ row: ["Package", inv.service_request.package_name] }]
        : []),
    { divider: true },
    ...items.flatMap((it) => {
      const name = it.label || it.description || it.name || "Item";
      const qty = it.qty ?? it.quantity ?? "";
      const rate = it.rate ?? "";
      const amt = money(it.amount);
      return [
        { text: name },
        { row: [qty !== "" && rate !== "" ? `${qty} x ${money(rate)}` : "", amt] },
      ];
    }),
    ...(items.length ? [{ divider: true }] : []),
    { row: ["Subtotal", money(inv.subtotal)] },
    ...(inv.gst_amount ? [{ row: [`GST ${inv.gst_rate || 0}%`, money(inv.gst_amount)] }] : []),
    { divider: true },
    { row: ["TOTAL", money(inv.total)], bold: true, size: "tall" },
    { blank: true },
    { text: `Status: ${(inv.payment_status || "").toUpperCase()}`, align: "center", bold: true },
    ...(inv.ebill_url ? [{ blank: true }, { text: `E-bill: ${inv.ebill_url}`, align: "center" }] : []),
    { blank: true },
    { text: "*** Thank you ***", align: "center" },
    { text: "www.rescigo.in", align: "center" },
  ];
  return { lines, cut: true, feedAfter: 4 };
}
